import { TMTimeTrackingAPI } from '@business/api/tm_time_tracking';
import { TimeMaterialId } from '@business/entities/tm';
import { TMTimeTrackingResponse, TMTimeTrackingId } from '@business/entities/tm/TMTimeTrackingResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { TMTimeTrackingFormData } from '@modules/timeMaterials/TimeMaterialOverlay/Tabs/AddEditTMTimeTrackingOverlay/AddEditTMTimeTrackingOverlay';
import { map, switchMap } from 'rxjs/operators';

export class TMTimeTrackingBloc extends BaseBloc {
  private readonly timeTrackingRepo = new Repository<TMTimeTrackingResponse>({
    getItemId: item => item.id,
  });

  readonly timeTrackings$ = this.timeTrackingRepo.items$;
  readonly selectTimeTracking = this.timeTrackingRepo.selectItem;

  readonly selectTimeTrackingByOrderId = (orderId: TimeMaterialId) =>
    this.timeTrackingRepo.items$.pipe(map(items => items.filter(item => item.orderId == orderId)));

  readonly selectTimeTrackingByProductItem = (estimateLineId: number) =>
    this.timeTrackingRepo.items$.pipe(map(items => items.filter(item => item.estimateItemId == estimateLineId)));

  onReset() {
    this.timeTrackingRepo.reset();
  }

  fetchTimeTrackings = (orderId?: TimeMaterialId, isPublic = false) => {
    return TMTimeTrackingAPI.fetchTimeTrackings(orderId, isPublic).pipe(
      this.timeTrackingRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  fetchTimeTracking = (id: TMTimeTrackingId) => {
    return TMTimeTrackingAPI.fetchTimeTracking(id).pipe(
      this.timeTrackingRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  createTimeTracking = (data: Partial<TMTimeTrackingFormData>) => {
    return TMTimeTrackingAPI.createTimeTracking(data).pipe(
      this.timeTrackingRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_time_tracking'),
      }),
    );
  };

  updateTimeTracking = (id: TMTimeTrackingId, data: Partial<TMTimeTrackingFormData>) => {
    return TMTimeTrackingAPI.updateTimeTracking(id, data).pipe(
      this.timeTrackingRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_time_tracking'),
      }),
    );
  };

  deleteTimeTracking = (id: TMTimeTrackingId) => {
    return showDeleteConfirmation('Delete Time Tracking', 'Do you really want to remove this tracked time?').pipe(
      switchMap(() => TMTimeTrackingAPI.deleteTimeTracking(id)),
      this.timeTrackingRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_time_tracking'),
      }),
    );
  };
}
